import React, { useState, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  BarChart3, MapPin, User, Building2, LineChart, Bot, HeartPulse, ShieldCheck,
} from 'lucide-react';
import useAuthScope from 'hooks/useAuthScope';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/Tabs';
import DateRangeFilter from '../../../components/dateRangeFilter/DateRangeFilter';
import GlobalTab from './tabs/GlobalTab';
import MunicipalityTab from './tabs/MunicipalityTab';
import DriverTab from './tabs/DriverTab';
import TypeCompanyTab from './tabs/TypeCompanyTab';
import TimelineTab from './tabs/TimelineTab';
import AiAnalysisTab from './tabs/AiAnalysisTab';
import AiPerformanceTab from './tabs/AiPerformanceTab';
import './DeliveryQuality.scss';

// Tabs que ignoran el filtro global de fechas porque tienen su propio control
// (auto-refresh, ventana relativa, etc).
const TABS_WITHOUT_GLOBAL_FILTER = new Set(['aiPerformance']);

const DeliveryQualityView = () => {
  const { t } = useTranslation('deliveryquality');

  const TABS = [
    { id: 'global', label: t('Global'), icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'municipality', label: t('Por comuna'), icon: <MapPin className="w-4 h-4" /> },
    { id: 'driver', label: t('Por repartidor'), icon: <User className="w-4 h-4" /> },
    { id: 'typeCompany', label: t('Tipo / Cliente'), icon: <Building2 className="w-4 h-4" /> },
    { id: 'timeline', label: t('Línea de tiempo'), icon: <LineChart className="w-4 h-4" /> },
    { id: 'aiAnalysis', label: t('Análisis IA'), icon: <Bot className="w-4 h-4" /> },
    { id: 'aiPerformance', label: t('Salud IA'), icon: <HeartPulse className="w-4 h-4" /> },
  ];

  // Tenant scope leido del JWT (no de useAuth().userData, que depende de
  // GET /users/:id que el backend no expone). Ver hooks/useAuthScope.
  const { roleType, companyId, logisticCompanyId } = useAuthScope();
  const [active, setActive] = useState('global');
  // Migrado al DateRangeFilter canónico (audit #7). El componente envía
  // (startYMD, endYMD, preset) → mantenemos start/end para el resto del view
  // (filtros y queries usan los YMD strings) y `preset` para el control.
  const [datePreset, setDatePreset] = useState('7d');
  const [range, setRange] = useState({
    start: dayjs().subtract(6, 'day').format('YYYY-MM-DD'),
    end: dayjs().format('YYYY-MM-DD'),
  });

  // Detecta el alcance (scope) del usuario logueado para filtrar todo el
  // dashboard a su tenant. Un usuario logistic ve solo paquetes de su
  // logistic_company; un usuario company ve solo los suyos.
  const scope = useMemo(() => {
    if (roleType === 'logistic' && logisticCompanyId) {
      return { logistic_company_id: logisticCompanyId };
    }
    if (roleType === 'company' && companyId) {
      return { company_id: companyId };
    }
    return {};
  }, [roleType, companyId, logisticCompanyId]);

  const filters = useMemo(
    () => ({
      date_from: range.start ? `${range.start}T00:00:00` : undefined,
      date_to: range.end ? `${range.end}T23:59:59` : undefined,
      ...scope,
    }),
    [range, scope],
  );

  // Canonical DateRangeFilter envía (startYMD, endYMD, preset) posicional.
  const handleRangeChange = useCallback((start, end, preset) => {
    setRange({ start, end });
    if (preset) setDatePreset(preset);
  }, []);

  // Presets equivalentes a los de la versión deliveryQuality original:
  // 7d, 30d, 60d, thisMonthToToday. La lista canónica además expone Hoy/Ayer/etc.
  const DQ_PRESETS = useMemo(
    () => [
      { key: '7d', quick: true },
      { key: '30d', quick: true },
      { key: '60d', quick: true },
      { key: 'thisMonthToToday', quick: true },
      { key: 'thisMonth', quick: false },
      { key: 'lastMonth', quick: false },
      { key: '90d', quick: false },
    ],
    [],
  );

  const showGlobalFilter = !TABS_WITHOUT_GLOBAL_FILTER.has(active);

  return (
    <div className="w-full min-w-0 p-4 sm:p-6 space-y-4 delivery-quality-view">
      <PageHeader
        icon={<ShieldCheck className="w-6 h-6 text-fg-muted" />}
        title={t('Calidad de entregas')}
        subtitle={t('Indicadores ponderados de calidad por repartidor, comuna, tipo de paquete y cliente.')}
      />

      <Tabs value={active} onValueChange={setActive}>
        <TabsList ariaLabel={t('Secciones de calidad de entregas')}>
          {TABS.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id} icon={tab.icon}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {showGlobalFilter && (
          <div className="mt-4">
            <DateRangeFilter value={datePreset} onChange={handleRangeChange} presets={DQ_PRESETS} />
          </div>
        )}

        {/* Cada tab se monta solo cuando está activo para no disparar todos los
            fetch a la vez. */}
        <TabsContent value="global" className="mt-4">
          {active === 'global' && <GlobalTab filters={filters} />}
        </TabsContent>
        <TabsContent value="municipality" className="mt-4">
          {active === 'municipality' && <MunicipalityTab filters={filters} />}
        </TabsContent>
        <TabsContent value="driver" className="mt-4">
          {active === 'driver' && <DriverTab filters={filters} />}
        </TabsContent>
        <TabsContent value="typeCompany" className="mt-4">
          {active === 'typeCompany' && <TypeCompanyTab filters={filters} />}
        </TabsContent>
        <TabsContent value="timeline" className="mt-4">
          {active === 'timeline' && <TimelineTab filters={filters} />}
        </TabsContent>
        <TabsContent value="aiAnalysis" className="mt-4">
          {active === 'aiAnalysis' && <AiAnalysisTab filters={filters} />}
        </TabsContent>
        <TabsContent value="aiPerformance" className="mt-4">
          {active === 'aiPerformance' && <AiPerformanceTab />}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DeliveryQualityView;
